// One media file plays at a time, whichever player starts it: the play bar, a listening
// room, an attachment in chat, a video in the viewer. Call audio and video (live streams,
// `srcObject`) are left alone.
//
// A listening room isn't paused (that would pause it for everyone, and its sync would
// start it again): starting something else here leaves it.

function isFile(m: EventTarget | null): m is HTMLMediaElement {
  return m instanceof HTMLMediaElement && !m.srcObject
}

/** Marks the listening room's player. */
const PARTY_ATTR = 'data-party-media'

export function installOnePlayer(leaveParty: () => void): () => void {
  /** Every file element that has played, each listened to directly: one taken out of the
   * page fires nothing the document sees, yet can still play (and be stopped). */
  const played = new Set<HTMLMediaElement>()
  const inParty = (m: HTMLMediaElement) => m.isConnected && !!m.closest(`[${PARTY_ATTR}]`)

  const onPlay = (e: Event): void => {
    const now = e.target as HTMLMediaElement
    played.add(now)
    let partyPlaying = false
    for (const m of played) {
      if (m === now) continue
      if (inParty(m)) {
        if (!m.paused) partyPlaying = true
        continue
      }
      if (!m.paused) m.pause()
      // Gone and quiet: forget it (its listener stays, and adds it back if it ever plays).
      if (!m.isConnected) played.delete(m)
    }
    if (partyPlaying && !inParty(now)) leaveParty()
  }
  // `play` doesn't bubble; capture sees each element's first one on its way down.
  const discover = (e: Event): void => {
    const m = e.target
    if (!isFile(m) || played.has(m)) return
    played.add(m)
    m.addEventListener('play', onPlay)
  }
  document.addEventListener('play', discover, true)

  // Audio devices changed (headphones switching to call mode and back, one plugged in or
  // out): a playing element can keep a dead output and stay silent while its clock runs
  // (Firefox). Pausing and playing again opens a fresh one on the current device.
  let settle: ReturnType<typeof setTimeout> | undefined
  const reopen = (): void => {
    clearTimeout(settle)
    settle = setTimeout(() => {
      for (const m of played) {
        if (!m.isConnected || m.paused) continue
        m.pause()
        void m.play().catch(() => undefined)
      }
    }, 500)
  }
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  md?.addEventListener?.('devicechange', reopen)
  return () => {
    clearTimeout(settle)
    md?.removeEventListener?.('devicechange', reopen)
    document.removeEventListener('play', discover, true)
    for (const m of played) m.removeEventListener('play', onPlay)
    played.clear()
  }
}
